import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Loader from '../components/Loader/Loader';
import withAdminProtection from '../hoc/withAdminProtection';
import withTicketProtection from '../hoc/withTicketProtection';
import withAuthProtection from '../hoc/withAuthProtection';
import { useDeletionStatus } from '../hooks/useDeletionStatus';
const DishDetailPage = lazy(() => import('../pages/DishDetailPage/DishDetailPage'));

const QueryProviderWrapper = lazy(() => import('../context/QueryProvider'));
const HomePage = lazy(() => import('../pages/HomePage/HomePage'));

// Lazy load некритичних сторінок
const AllRecipesPage = lazy(() => import('../pages/AllRecipesPage/AllRecipesPage'));
const CategoriesPage = lazy(() => import('../pages/CategoriesPage/CategoriesPage'));
const CollectionsDirectoryPage = lazy(() => import('../pages/CollectionsDirectoryPage/CollectionsDirectoryPage'));
const CollectionDetailPage = lazy(() => import('../pages/CollectionDetailPage/CollectionDetailPage'));
const MealPlannerPage = lazy(() => import('../pages/MealPlannerPage/MealPlannerPage'));

const AddRecipePage = lazy(() => import('../pages/AddRecipePage/AddRecipePage'));
const EditRecipePage = lazy(() => import('../pages/EditRecipePage/EditRecipePage'));
const TranslateRecipePage = lazy(() => import('../pages/TranslateRecipePage/TranslateRecipePage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage/ProfilePage'));
const SearchPage = lazy(() => import('../pages/SearchPage/SearchPage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage/NotificationsPage'));

// Адмін панель - окремий chunk
const AdminPage = lazy(() => import('../pages/AdminPage/AdminPage'));

// Stripe та підтримка - окремі chunks
const PremiumPage = lazy(() => import('../pages/PremiumPage/PremiumPage'));
const SupportPage = lazy(() => import('../pages/SupportPage/SupportPage').then(module => ({ default: module.SupportPage })));
const TicketChat = lazy(() => import('../components/Support/TicketChat'));

// Статичні сторінки
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy/PrivacyPolicy'));
const TermsOfService = lazy(() => import('../pages/TermsOfService/TermsOfService'));
const ContactPage = lazy(() => import('../pages/ContactPage/ContactPage'));
const GDPRDashboard = lazy(() => import('../components/GDPRDashboard/GDPRDashboard'));
const DownloadPage = lazy(() => import('../pages/DownloadPage/DownloadPage'));

const ProtectedAdminPage = withAdminProtection(AdminPage);
const ProtectedTranslateRecipePage = withAdminProtection(TranslateRecipePage);
const ProtectedTicketChat = withTicketProtection(TicketChat);
const ProtectedGDPRDashboard = withAuthProtection(GDPRDashboard);
const ProtectedSupportPage = withAuthProtection(SupportPage);
const ProtectedAddRecipePage = withAuthProtection(AddRecipePage);
const ProtectedEditRecipePage = withAuthProtection(EditRecipePage);

const AppRouter = () => {
  const { currentLanguage } = useLanguage();
  
  // Додаємо перевірку статусу видалення
  const { deletionStatus } = useDeletionStatus();

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/" element={<Navigate to={`/${currentLanguage}`} replace />} />
        <Route path="/:lang" element={<HomePage />} />
        <Route path="/:lang/recipes" element={<Suspense fallback={<Loader />}><QueryProviderWrapper><AllRecipesPage /></QueryProviderWrapper></Suspense>} />
        <Route path="/:lang/categories" element={<CategoriesPage />} />
        <Route path="/:lang/collections" element={<CollectionsDirectoryPage />} />
        <Route path="/:lang/collections/:slug" element={<CollectionDetailPage />} />
        <Route path="/:lang/meal-planner" element={<MealPlannerPage />} />
        <Route path="/meal-planner" element={<Navigate to={`/${currentLanguage}/meal-planner`} replace />} />
        <Route path="/:lang/recipe/:id" element={<DishDetailPage />} />
        <Route path="/:lang/add-recipe" element={<Suspense fallback={<Loader />}><QueryProviderWrapper><ProtectedAddRecipePage /></QueryProviderWrapper></Suspense>} />
        <Route path="/:lang/edit-recipe/:id" element={<Suspense fallback={<Loader />}><QueryProviderWrapper><ProtectedEditRecipePage /></QueryProviderWrapper></Suspense>} />
        <Route path="/translate-recipe/:recipeId" element={<ProtectedTranslateRecipePage />} />
        <Route path="/:lang/translate-recipe/:recipeId" element={<ProtectedTranslateRecipePage />} />
        <Route path="/:lang/profile/:userHandle" element={<ProfilePage />} />
        <Route path="/:lang/search" element={<SearchPage />} />
        <Route path="/:lang/admin" element={<ProtectedAdminPage />} />
        <Route path="/:lang/premium" element={<PremiumPage />} />
        <Route path="/:lang/support" element={<ProtectedSupportPage />} />
        <Route path="/:lang/support/ticket/:ticketId" element={<ProtectedTicketChat />} />
        <Route path="/:lang/notifications" element={<NotificationsPage />} />
        <Route path="/notifications" element={<Navigate to={`/${currentLanguage}/notifications`} replace />} />
        <Route path="/:lang/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/:lang/terms-of-service" element={<TermsOfService />} />
        <Route path="/:lang/contact" element={<ContactPage />} />
        <Route path="/:lang/gdpr-dashboard" element={<ProtectedGDPRDashboard />} />
        <Route path="/:lang/download" element={<DownloadPage />} />
        <Route path="/privacy-policy" element={<Navigate to={`/${currentLanguage}/privacy-policy`} replace />} />
        <Route path="/terms-of-service" element={<Navigate to={`/${currentLanguage}/terms-of-service`} replace />} />
        <Route path="/contact" element={<Navigate to={`/${currentLanguage}/contact`} replace />} />
        <Route path="/gdpr-dashboard" element={<Navigate to={`/${currentLanguage}/gdpr-dashboard`} replace />} />
        <Route path="/download" element={<Navigate to={`/${currentLanguage}/download`} replace />} />
        <Route path="/collections" element={<Navigate to={`/${currentLanguage}/collections`} replace />} />
        <Route path="*" element={<Navigate to={`/${currentLanguage}`} replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
